import { Grid, Menu, MenuItem, Theme } from '@mui/material';
import { createStyles, withStyles } from '@mui/styles';
import { Location } from '@nx-smartmonkey/shared/domain';
import { MarkerDomainTypes, RoutalLoader } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { useRetrieveClient } from '../../../../hooks/client/useRetrieveClient';
import { useRetrieveFlatClients } from '../../../../hooks/client/useRetrieveFlatClients';
import { useUpdateClient } from '../../../../hooks/client/useUpdateClient';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectResources } from '../../../../hooks/project/useRetrieveActiveProjectResources';
import { useRetrieveRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useSetActiveLanguage } from '../../../../hooks/translations/useSetActiveLanguage';
import { Map } from '../../../../react-components/Map';
import Text from '../../../../react-components/Text';
import eventsPanel, { PageViewPages } from '../../../../services/events';
import { getLocationFromCoordinates } from '../../../../services/requests';
import { ClientsFromScratchDialog } from '../ClientsFromScratchDialog';

const styles = ({ breakpoints }: Theme) =>
  createStyles({
    mapContextRootMenu: {
      width: `240px`,
    },
    mapContextMenuItem: {
      height: `42px`,
    },
  });

interface ProjectAssetsClientsMapProps {
  classes: {
    mapContextRootMenu: string;
    mapContextMenuItem: string;
  };
}

type ClientsMapContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
  coordinates: {
    lat: number;
    lng: number;
  };
};

export const ProjectAssetsClientsMap = withStyles(styles)(({ classes }: ProjectAssetsClientsMapProps) => {
  const { detailId: rightPanelClientId, setRightPanelInfo } = useRetrieveRightPanelInfo();

  const { data: activeLanguage } = useSetActiveLanguage();

  const { data: activeProject } = useRetrieveActiveProject();

  const { data: projectResources } = useRetrieveActiveProjectResources();

  const [mapContextAnchor, setMapContextAnchor] = useState<ClientsMapContextAnchor | undefined>(undefined);

  const { data: rightPanelClient } = useRetrieveClient({ clientId: rightPanelClientId });

  const [createClientModal, setCreateClientModal] = useState<boolean>(false);

  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>(undefined);

  const { run: updateClient } = useUpdateClient();

  const { data: flatClients, isLoading } = useRetrieveFlatClients();

  useEffect(() => {
    eventsPanel.pageView(PageViewPages.clients);
  }, []);

  /**
   * Check against the quota if new clients can be added
   */
  const canAdd = () => {
    if (!activeProject || !projectResources || !flatClients) {
      return false;
    }
    return flatClients.length < projectResources.max_clients;
  };

  return (
    <>
      <Grid
        container
        direction="column"
        style={{ padding: `22px`, paddingTop: 0, height: `100%`, width: `100%` }}
        wrap="nowrap"
      >
        {isLoading ? (
          <Grid container alignItems="center" justifyContent="center" style={{ height: `100%`, width: `100%` }}>
            <RoutalLoader width={60} />
          </Grid>
        ) : (
          <div
            style={{
              position: `relative` as any,
              height: `100%`,
              width: `100%`,
            }}
          >
            <div
              style={{
                position: `absolute` as any,
                top: 0,
                left: 0,
                height: `100%`,
                width: `100%`,
              }}
            >
              <Map
                key="map"
                height="100%"
                zoomControl
                language={activeLanguage?.locale ?? `en`}
                onContextualMenu={async (event: any) => {
                  try {
                    const result = await getLocationFromCoordinates([
                      { lat: event.latLng.lat(), lng: event.latLng.lng() },
                    ]);
                    setSelectedLocation(result[0]);
                  } catch (error) {
                    setSelectedLocation(undefined);
                  }

                  setMapContextAnchor({
                    screenPosition: {
                      top: event.domEvent.y,
                      left: event.domEvent.x,
                    },
                    coordinates: {
                      lat: event.latLng.lat(),
                      lng: event.latLng.lng(),
                    },
                  });
                }}
                markerOnClick={(e: any, id: string, isRouteSpecificMarker: boolean) => {
                  setRightPanelInfo({
                    detailType: `client`,
                    detailId: `${id}`,
                    detailTab: null,
                  });
                }}
                markers={(flatClients ?? [])
                  .filter((cF) => cF.isValidLocation())
                  .map((client) => ({
                    id: client.id,
                    label: `${client.lat}, ${client.lng}`,
                    lat: client.lat,
                    lng: client.lng,
                    kind: `default` as MarkerDomainTypes,
                    selected: rightPanelClientId === client.id,
                  }))}
              />

              {/* MAP CONTEXTUAL MENU */}
              {mapContextAnchor && (
                <Menu
                  onClose={() => {
                    setMapContextAnchor(undefined);
                    setSelectedLocation(undefined);
                  }}
                  open={Boolean(mapContextAnchor)}
                  anchorReference="anchorPosition"
                  anchorPosition={mapContextAnchor && mapContextAnchor.screenPosition}
                  classes={{ list: classes.mapContextRootMenu }}
                >
                  {selectedLocation === undefined && <RoutalLoader />}
                  {selectedLocation === null && (
                    <Text variant="body" tagName="p" style={{ padding: `16px` }} intlId="location_nothing_found" />
                  )}
                  {selectedLocation && (
                    <>
                      <p style={{ padding: `16px`, fontSize: `11px` }}>{selectedLocation.label}</p>
                      <MenuItem
                        disabled={!canAdd()}
                        className={classes.mapContextMenuItem}
                        onClick={() => {
                          setMapContextAnchor(undefined);
                          setCreateClientModal(true);
                        }}
                      >
                        <Text style={{ cursor: `pointer` }} intlId="clients.details.map.create_new" />
                      </MenuItem>
                      {rightPanelClient && (
                        <MenuItem
                          className={classes.mapContextMenuItem}
                          onClick={() => {
                            updateClient({ client: rightPanelClient, changes: { location: selectedLocation } });
                            setMapContextAnchor(undefined);
                          }}
                        >
                          <Text style={{ cursor: `pointer` }} intlId="clients.details.map.add_to_current" />
                        </MenuItem>
                      )}
                    </>
                  )}
                </Menu>
              )}
            </div>
          </div>
        )}
      </Grid>

      <ClientsFromScratchDialog
        open={createClientModal}
        initialLocation={selectedLocation}
        handleCloseDialog={() => setCreateClientModal(false)}
      />
    </>
  );
});
